import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-client';

import { INSIGHT_TYPE_TO_WIRE } from '@/features/intelligence-dashboard/insight.types';
import type { InsightType } from '@/features/intelligence-dashboard/insight.types';

/**
 * Endpoint for AI-generated insight explanations (SCRUM-49).
 * nginx proxies relative /api/* URLs to bankiq-api.
 */
const EXPLANATION_URL = '/api/intelligence/insights/explanation';

/** Demo tenant — shared across all frontend API calls. */
// SCRUM-643: tenant from the App ID token claim (SCRUM-102), no hard-coded header.

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ExplanationRequest {
  /** UI InsightType key ('flight' | 'dormant' | 'smb' | 'relgap') */
  insightType: InsightType;
  facts: { label: string; value: string }[];
}

export interface AiExplanationContent {
  content: string;
  aiGenerated: boolean;
  model: string;
}

// ── Fetcher ───────────────────────────────────────────────────────────────────

async function fetchExplanation(req: ExplanationRequest): Promise<AiExplanationContent> {
  const res = await apiFetch(EXPLANATION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      // Send as integer — ASP.NET Core deserializes enums as integers by default.
      // No JsonStringEnumConverter configured globally (F-1 audit fix).
      insightType: INSIGHT_TYPE_TO_WIRE[req.insightType],
      facts: req.facts,
    }),
  });

  if (!res.ok) {
    throw new Error(`POST ${EXPLANATION_URL} failed: ${res.status.toString()} ${res.statusText}`);
  }

  const data = (await res.json()) as AiExplanationContent;

  // Treat an empty/unavailable fallback as a retryable transient failure.
  // The backend returns {content:'', aiGenerated:false} on cold-start or gateway
  // unavailability. A 200-with-empty-body is NOT a React Query error by default,
  // so we throw manually to trigger retry and prevent 5-min caching of the blank state.
  // SCRUM-236: belt-and-suspenders UI hardening alongside Min Scale 1 infra fix.
  if (!data.aiGenerated) {
    throw new Error('AI gateway unavailable (aiGenerated:false) — will retry');
  }

  return data;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Fetches an AI-generated explanation for a single customer insight (SCRUM-49).
 * Returns a 2–3 sentence paragraph explaining why the customer was flagged.
 *
 * On a cold-start or transient gateway unavailability, the fetcher throws and
 * React Query retries up to 2 times with exponential backoff (1s → 2s, capped 4s).
 * If all retries fail the UI falls back silently to the existing static signal reason —
 * the explanation is an enhancement, not core functionality.
 *
 * The cache (staleTime: 5 min) only holds successful aiGenerated:true results;
 * the fetcher throws on aiGenerated:false so blank results are never cached.
 *
 * SCRUM-236: self-healing retry added alongside Min Scale 1 infra fix.
 * Implemented under SCRUM-221 (SCRUM-218 Dashboard AI Insight Text).
 */
export function useInsightExplanation(req: ExplanationRequest) {
  const factsKey = req.facts.map((f) => `${f.label}:${f.value}`).join('|');

  return useQuery<AiExplanationContent>({
    queryKey: ['insight-explanation', req.insightType, factsKey],
    queryFn: () => fetchExplanation(req),
    staleTime: 5 * 60 * 1000, // 5 min — only reached on aiGenerated:true (fetcher throws otherwise)
    retry: 2,
    retryDelay: (failureCount) => Math.min(1000 * 2 ** failureCount, 4000), // 1s → 2s → cap 4s
    throwOnError: false,
  });
}
